/**
 * Datasets 목록의 행 모델.
 *
 * 정렬 키가 되는 값만 public 으로 노출한다 — c6 `DataTable` 의 컬럼 key 는 행의 public 필드에서
 * 고르고, 기본 `compare` 가 그 값을 그대로 비교하기 때문이다. 등록 시각을 ms 정수로 들고 있어
 * 문자열 정렬로 새는 일이 없다(규칙 §7).
 *
 * Probe 칸은 상태 순서(`PROBE_STATES`)로 정렬한다. Probe 가 없는 행은 오름차순에서 뒤다(c6 기본 비교의 null 규칙).
 *
 * Owns:
 * - 행 식별 · 클릭 활성화 · 행 액션 배선, 정렬에 쓰는 값의 모양.
 *
 * Does not own:
 * - 조회 · 필터 — `DatasetTableSource.ts`. 셀 표현 — `datasetColumns.tsx`.
 */
import type { ReactNode } from "react";
import { TableRow } from "@carbon6/c6-frontend-design";
import type { Dataset, DatasetSourceKind, LatestProbe } from "../domain/dataset";
import { PROBE_STATES } from "../domain/probe";

export type DatasetRowOptions = {
  /** 행 클릭 — 창을 열려면 이름까지 필요해 Dataset 전체를 넘긴다. */
  onOpen: (dataset: Dataset) => void;
  /** 행 우측 액션. 행마다 자기 Dataset 을 닫아 넘긴다. */
  renderActions?: (dataset: Dataset) => ReactNode;
};

export class DatasetTableRow extends TableRow {
  readonly uuid: string;
  readonly displayName: string;
  readonly sourceRef: string;
  readonly sourceKind: DatasetSourceKind;
  readonly createdAtMs: number;
  readonly hiddenAtMs: number | null;
  readonly latestProbe: LatestProbe | null;

  private readonly dataset: Dataset;
  private readonly onOpen: (dataset: Dataset) => void;

  constructor(dataset: Dataset, { onOpen, renderActions }: DatasetRowOptions) {
    super(renderActions === undefined ? null : () => renderActions(dataset));
    this.dataset = dataset;
    this.uuid = dataset.uuid;
    this.displayName = dataset.displayName;
    this.sourceRef = dataset.sourceRef;
    this.sourceKind = dataset.sourceKind;
    this.createdAtMs = dataset.createdAtMs;
    this.hiddenAtMs = dataset.hiddenAtMs;
    this.latestProbe = dataset.latestProbe;
    this.onOpen = onOpen;
  }

  override compare(other: this, columnKey: string): number {
    if (columnKey !== "latestProbe") {
      return super.compare(other, columnKey);
    }
    const mine = this.latestProbe;
    const theirs = other.latestProbe;
    if (mine === null || theirs === null) {
      return mine === theirs ? 0 : mine === null ? 1 : -1;
    }
    return PROBE_STATES.indexOf(mine.state) - PROBE_STATES.indexOf(theirs.state);
  }

  override getRowId(): string {
    return this.uuid;
  }

  /**
   * 항상 보인다 — 검색 · 숨김 축은 source 가 행을 만들기 전에 이미 걸렀다. c6 계약상
   * `canShow()` 는 행 데이터의 순수 함수여야 하므로 여기서 외부 조건을 읽지 않는다.
   */
  override canShow(): boolean {
    return true;
  }

  /** 일괄 액션이 없는 목록이라 선택 축을 두지 않는다. */
  override canSelect(): boolean {
    return false;
  }

  override get isRowClickable(): boolean {
    return true;
  }

  override onRowClick(): void {
    this.onOpen(this.dataset);
  }

  get isHidden(): boolean {
    return this.hiddenAtMs !== null;
  }
}
